/**
 * 企業側メッセージ受信箱ページ
 * スレッドベースのメッセージ管理画面
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Archive as ArchiveIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useMessageThreads } from '../../hooks/useMessageThreads';
import { useCompanyId } from '../../hooks/useCompanyId';
import { ThreadStatus } from '../../types/message-thread';
import { CompanyLayout } from '../../components/Layout/CompanyLayout';
import { ThreadList } from '../../components/MessageThread/ThreadList';
import { ThreadConversation } from '../../components/MessageThread/ThreadConversation';
import { ThreadFilters } from '../../components/MessageThread/ThreadFilters';

export const MessageInboxPage: React.FC = () => {
  const {
    companyId,
    loading,
    error: companyError,
    isCompanyUser,
  } = useCompanyId();
  const [showFilters, setShowFilters] = useState(false);

  // レスポンシブ対応
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showConversation, setShowConversation] = useState(false);

  const initialQuery = useMemo(
    () => ({
      limit: 20,
      sortBy: 'lastMessageAt' as const,
      sortOrder: 'DESC' as const,
    }),
    []
  );

  const {
    threads,
    currentThread,
    uiState,
    pagination,
    loadThreads,
    loadThreadDetail,
    sendMessage,
    updateThreadStatus,
    updateFilters,
    updateSort,
    loadNextPage,
    loadPrevPage,
    hasNextPage,
    hasPrevPage,
    totalUnread,
  } = useMessageThreads({
    userType: 'company',
    companyId: typeof companyId === 'number' ? companyId : undefined,
    autoLoad: !loading && typeof companyId === 'number', // ローディング完了かつ企業IDが取得できた場合のみ自動ロード
    initialQuery,
  });

  // スレッド選択
  const handleSelectThread = async (threadId: string) => {
    await loadThreadDetail(threadId);

    // モバイルでは会話画面に切り替え
    if (isMobile) {
      setShowConversation(true);
    }
  };

  // 戻るボタン処理（モバイル用）
  const handleBackToList = () => {
    setShowConversation(false);
  };

  // メッセージ送信
  const handleSendMessage = async (messageBody: string) => {
    if (!uiState.selectedThreadId) return;

    await sendMessage(uiState.selectedThreadId, {
      messageBody,
    });
  };

  // スレッド操作
  const handleArchiveThread = async (threadId: string) => {
    await updateThreadStatus(threadId, ThreadStatus.ARCHIVED);
  };

  const handleActivateThread = async (threadId: string) => {
    await updateThreadStatus(threadId, ThreadStatus.ACTIVE);
  };

  // リフレッシュ
  const handleRefresh = () => {
    loadThreads();
  };

  // デバッグログ
  console.log('🔍 [MessageInboxPage] State:', {
    companyId,
    loading,
    error: companyError,
    isCompanyUser,
    typeOfCompanyId: typeof companyId,
  });

  // ローディング中
  if (loading) {
    return (
      <CompanyLayout>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </Container>
      </CompanyLayout>
    );
  }

  // エラーまたは企業ユーザーでない場合
  if (companyError || !isCompanyUser || typeof companyId !== 'number') {
    return (
      <CompanyLayout>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error">
            {companyError ||
              '企業IDが取得できませんでした。ログインし直してください。'}
          </Alert>
        </Container>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <Container maxWidth="xl" sx={{ py: 2, px: { xs: 1, sm: 2, md: 3 } }}>
        {/* ヘッダー */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h4" component="h1">
              メッセージ受信箱
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="更新">
                <IconButton
                  onClick={handleRefresh}
                  disabled={uiState.isLoading}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* 統計情報 */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Chip
              label={`未読: ${totalUnread}件`}
              color={totalUnread > 0 ? 'error' : 'default'}
              variant={totalUnread > 0 ? 'filled' : 'outlined'}
            />
            <Chip
              label={`総スレッド数: ${pagination.total}件`}
              variant="outlined"
            />
          </Box>

          {/* エラー表示 */}
          {uiState.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uiState.error}
            </Alert>
          )}
        </Box>

        {/* メインコンテンツ */}
        {isMobile ? (
          /* モバイル版: スライドパネル方式 */
          <Paper
            sx={{
              height: 'calc(100vh - 160px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {showConversation ? (
              /* 会話画面 */
              <>
                {/* 戻るボタン付きヘッダー */}
                <Box
                  sx={{
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <IconButton onClick={handleBackToList} size="small">
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="h6">メッセージ</Typography>
                </Box>

                {/* 会話エリア */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {uiState.selectedThreadId ? (
                    <ThreadConversation
                      threadDetail={currentThread}
                      loading={uiState.isLoadingDetail}
                      sending={uiState.isSending}
                      onSendMessage={handleSendMessage}
                      userType="company"
                      canReply={currentThread?.canReply ?? true}
                    />
                  ) : (
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                      }}
                    >
                      <Typography variant="body1">
                        スレッドを選択してメッセージを表示
                      </Typography>
                    </Box>
                  )}
                </Box>
              </>
            ) : (
              /* スレッド一覧 */
              <>
                {/* フィルター */}
                <Box
                  sx={{
                    p: { xs: 1, sm: 2 },
                    borderBottom: 1,
                    borderColor: 'divider',
                    overflow: 'hidden', // 横スクロール防止
                  }}
                >
                  <ThreadFilters
                    filters={uiState.filters}
                    onFiltersChange={updateFilters}
                    userType="company"
                  />
                </Box>

                {/* スレッドリスト */}
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <ThreadList
                    threads={threads}
                    selectedThreadId={uiState.selectedThreadId}
                    onSelectThread={handleSelectThread}
                    loading={uiState.isLoading}
                    userType="company"
                    onArchive={handleArchiveThread}
                    onActivate={handleActivateThread}
                    pagination={pagination}
                    onNextPage={loadNextPage}
                    onPrevPage={loadPrevPage}
                    hasNextPage={hasNextPage}
                    hasPrevPage={hasPrevPage}
                  />
                </Box>
              </>
            )}
          </Paper>
        ) : (
          /* PC版: 従来の左右分割 */
          <Grid container spacing={2} sx={{ height: 'calc(100vh - 200px)' }}>
            {/* スレッド一覧 */}
            <Grid item md={4}>
              <Paper
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* フィルター */}
                <Box
                  sx={{
                    p: { xs: 1, sm: 2 },
                    borderBottom: 1,
                    borderColor: 'divider',
                    overflow: 'hidden', // 横スクロール防止
                  }}
                >
                  <ThreadFilters
                    filters={uiState.filters}
                    onFiltersChange={updateFilters}
                    userType="company"
                  />
                </Box>

                {/* スレッドリスト */}
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <ThreadList
                    threads={threads}
                    selectedThreadId={uiState.selectedThreadId}
                    onSelectThread={handleSelectThread}
                    loading={uiState.isLoading}
                    userType="company"
                    onArchive={handleArchiveThread}
                    onActivate={handleActivateThread}
                    pagination={pagination}
                    onNextPage={loadNextPage}
                    onPrevPage={loadPrevPage}
                    hasNextPage={hasNextPage}
                    hasPrevPage={hasPrevPage}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* 会話エリア */}
            <Grid item md={8}>
              <Paper
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {uiState.selectedThreadId ? (
                  <ThreadConversation
                    threadDetail={currentThread}
                    loading={uiState.isLoadingDetail}
                    sending={uiState.isSending}
                    onSendMessage={handleSendMessage}
                    userType="company"
                    canReply={currentThread?.canReply ?? true}
                  />
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    <Typography variant="body1">
                      スレッドを選択してメッセージを表示
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </Container>
    </CompanyLayout>
  );
};
